/**
 * How many tiles the views page draws while its library loads.
 *
 * The skeleton used to be three fixed cards. A first visit (signed out, nothing saved)
 * loads to ONE tile — Build View — so on a phone, where the grid is one column, the
 * gallery under it started two cards too low and jumped up the moment the list arrived.
 *
 * The count is the last answer `GET /api/views` gave THIS identity, plus the Build View
 * tile that always follows the saved views. It is stored with whose answer it was:
 *  - signed in: a short fingerprint of the session token (never the token itself). It is
 *    read synchronously at first paint, before the account itself is known, and a new
 *    sign-in (another account, or the same one again) simply starts without a count;
 *  - signed out: the browser's anon id, the identity the server lists a guest's views by.
 * An answer recorded for anyone else is ignored and dropped, and signing out forgets it
 * outright (`forgetViewsCount`, wired in Home), so one person's count never shapes the
 * next person's page.
 *
 * With no usable count:
 *  - a signed-in visitor gets the old three-card skeleton (`null`), since an account
 *    usually has views and three is what the page always drew;
 *  - a guest gets the Build View tile alone (0), which is exactly a new visitor's page.
 * It only ever shapes a placeholder — the server's list is still all the page renders.
 */
import { getUserToken } from "../../lib/api";

const KEY = "gooner.viewsSkeleton";
/** The first version's key: a bare number with no owner. Never read, only removed. */
const LEGACY_KEY = "gooner.viewsCount";
/** api.ts's anon id. Read raw: its getter mints an id when there is none. */
const ANON_ID_KEY = "gp:anon-id";
/** A placeholder this long is already far below the fold on every screen. */
export const MAX_VIEWS = 11;

/** FNV-1a, 32 bits. Enough to tell two sessions apart; useless for recovering a token. */
function fingerprint(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** Who a stored count belongs to, or null when there is no stable identity to key on. */
function identity(): { who: string; signedIn: boolean } | null {
  try {
    const token = getUserToken();
    if (token) return { who: `u:${fingerprint(token)}`, signedIn: true };
    const anon = localStorage.getItem(ANON_ID_KEY);
    return anon ? { who: `a:${anon}`, signedIn: false } : null;
  } catch {
    return null;
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* blocked storage holds nothing to drop */
  }
}

export function rememberViewsCount(n: number): void {
  drop(LEGACY_KEY);
  const id = identity();
  if (!id) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ who: id.who, n: Math.max(0, Math.floor(n)) }));
  } catch {
    /* storage full or blocked — the skeleton falls back to its no-count shape */
  }
}

/** Signing out (or the account going away): the count was that account's, not this browser's. */
export function forgetViewsCount(): void {
  drop(KEY);
  drop(LEGACY_KEY);
}

/**
 * Saved-view placeholders to draw before the Build View tile: this identity's last count
 * (capped), 0 for a guest with none, or null for a signed-in visitor with none — the
 * caller then draws the old three-card skeleton.
 */
export function skeletonViewCount(): number | null {
  const id = identity();
  const fallback = id?.signedIn ? null : 0;
  let saved: { who?: unknown; n?: unknown } | null = null;
  try {
    const raw = localStorage.getItem(KEY);
    saved = raw ? (JSON.parse(raw) as { who?: unknown; n?: unknown }) : null;
  } catch {
    saved = null;
  }
  if (!saved || typeof saved !== "object") return fallback;
  if (!id || saved.who !== id.who) {
    drop(KEY); // someone else's answer: never reuse it
    return fallback;
  }
  const n = saved.n;
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return fallback;
  return Math.min(Math.floor(n), MAX_VIEWS);
}
